import { useState, useEffect, useCallback, useMemo } from 'react';
import type { DfePositionConflict } from '@/lib/api';
import { useNavigate, Link } from 'react-router-dom';
import { api, DfeInventoryEntry } from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { FavoriteButton } from '../../../components/FavoriteButton';
import { useInventory } from '../hooks/useInventory';
import { useTransferHistory } from '../hooks/useTransferHistory';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { InventoryGrid } from '../components/InventoryGrid';
import { TransferItemDialog } from '../components/TransferItemDialog';
import { DeleteItemDialog } from '../components/DeleteItemDialog';
import { TransferHistoryList } from '../components/TransferHistoryList';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Settings, AlertTriangle } from 'lucide-react';

export function InventoryPage() {
  const { user } = useAuth();
  const isAdmin = useMemo(
    () => user?.roles?.some((r) => r === 'admin' || r === 'litcraft_admin') ?? false,
    [user?.roles],
  );
  const { search, debouncedSearch, handleSearchChange } = useDebouncedSearch();
  const [fungibility, setFungibility] = useState<string>('all');
  const [sortBy, setSortBy] = useState('name');

  const { items, total, loading, error, refresh, loadMore, hasMore } = useInventory({
    search: debouncedSearch || undefined,
    fungibility: fungibility === 'all' ? undefined : fungibility,
    sortBy,
  });

  // Fetch position conflicts from server (covers all items, not just current page)
  const [positionConflicts, setPositionConflicts] = useState<DfePositionConflict[]>([]);
  useEffect(() => {
    api
      .getDfePositionConflicts()
      .then((data) => setPositionConflicts(data.conflicts))
      .catch(() => {
        /* non-critical */
      });
  }, []);

  const conflictItemDefIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of positionConflicts) {
      for (const i of c.items) ids.add(i.itemDefId);
    }
    return ids;
  }, [positionConflicts]);

  const [transferItem, setTransferItem] = useState<DfeInventoryEntry | null>(null);
  const [deleteItem, setDeleteItem] = useState<DfeInventoryEntry | null>(null);
  const navigate = useNavigate();

  const handleTransfer = useCallback(
    async (toUsername: string, quantity?: number) => {
      if (!transferItem) return;

      if (transferItem.fungibility === 'fungible') {
        await api.transferDfeItem({
          toUsername,
          itemDefId: transferItem.itemDefId,
          quantity: quantity || 1,
          category: transferItem.category || undefined,
          position: transferItem.position || undefined,
        });
      } else {
        await api.transferDfeItem({
          toUsername,
          itemId: transferItem.itemId!,
        });
      }
      refresh();
    },
    [transferItem, refresh],
  );

  const handleDelete = useCallback(
    async (quantity?: number) => {
      if (!deleteItem) return;

      if (deleteItem.fungibility === 'fungible') {
        await api.burnDfeFungibleItems({
          itemDefId: deleteItem.itemDefId,
          quantity: quantity || 1,
          category: deleteItem.category || undefined,
          position: deleteItem.position || undefined,
        });
      } else {
        await api.burnDfeItem(deleteItem.itemId!);
      }
      refresh();
    },
    [deleteItem, refresh],
  );

  const handleItemClick = useCallback(
    (item: DfeInventoryEntry) => {
      if (item.itemId) {
        navigate(`/a/arche_dfe/item/${item.itemId}`);
      }
    },
    [navigate],
  );

  return (
    <div className="max-w-6xl mx-auto px-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">My Inventory</h1>
            <FavoriteButton archeId="arche_dfe" />
          </div>
          <p className="text-muted-foreground text-sm">{total} items</p>
        </div>
        {isAdmin && (
          <Link to="/a/arche_dfe/admin">
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4 mr-2" />
              Admin
            </Button>
          </Link>
        )}
      </div>

      <Tabs defaultValue="inventory">
        <TabsList>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="mt-4">
          <div className="flex gap-3 mb-6 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search items..."
                className="pl-9"
              />
            </div>
            <Select value={fungibility} onValueChange={setFungibility}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="fungible">Fungible</SelectItem>
                <SelectItem value="non_fungible">Non-Fungible</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="acquired_at">Recently Acquired</SelectItem>
                <SelectItem value="quantity">Quantity</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div className="text-center py-4 text-destructive mb-4">
              <p>{error}</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={refresh}>
                Retry
              </Button>
            </div>
          )}

          {positionConflicts.length > 0 && (
            <div className="flex items-start gap-2 p-3 mb-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm">
              <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
              <div>
                <span className="font-medium">Position conflicts detected: </span>
                {positionConflicts.map((c, i) => (
                  <span key={`${c.category}:${c.position}`}>
                    {i > 0 && ', '}
                    {c.category}/{c.position}
                  </span>
                ))}
              </div>
            </div>
          )}

          <InventoryGrid
            items={items}
            loading={loading}
            conflictItemDefIds={conflictItemDefIds}
            onTransfer={setTransferItem}
            onDelete={setDeleteItem}
            onClick={handleItemClick}
          />

          {hasMore && (
            <div className="text-center mt-6">
              <Button variant="outline" onClick={loadMore} disabled={loading}>
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <TransferHistoryContent userId={user?.userId ?? ''} />
        </TabsContent>
      </Tabs>

      <TransferItemDialog
        item={transferItem}
        open={!!transferItem}
        onClose={() => setTransferItem(null)}
        onConfirm={handleTransfer}
      />

      <DeleteItemDialog
        item={deleteItem}
        open={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

function TransferHistoryContent({ userId }: { userId: string }) {
  const { transfers, loading, error, loadMore, hasMore } = useTransferHistory();

  return (
    <>
      {error && (
        <div className="text-center py-4 text-destructive mb-4">
          <p>{error}</p>
        </div>
      )}
      <TransferHistoryList
        transfers={transfers}
        currentUserId={userId}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={loadMore}
      />
    </>
  );
}
